import type { User } from '@ctcells/contract'
import { create } from 'zustand'

import type { Permission } from '../paths.ts'
import { safeStorage } from '../storage.ts'
import { resolvePermissions } from './permissionOperations.ts'

/**
 * 자격 보관 키. **값을 넣고 빼는 것은 `@ctcells/api` 의 토큰 저장소가 한다** —
 * 여기 있는 이유는 부팅 시 「복구할 세션이 있나」를 이 스토어가 먼저 알아야 하기 때문이다.
 * 🔴 전부 `sessionStorage` 다. 장비 PC 는 공용 단말이라 영속 저장은 사고다.
 */
export const AUTH_STORAGE_KEYS = {
  refresh: 'ctcells:auth:refresh',
  user: 'ctcells:auth:user',
  session: 'ctcells:auth:session',
  /** `GET /auth/me` 의 `operations` — 새로고침 뒤 첫 응답 전에도 판정이 서게(역할과 같은 자리 · 같은 수명) */
  operations: 'ctcells:auth:operations',
} as const

/**
 * 인증 세션. 판정은 서버(L2 게이트웨이)가 한다 — 프론트는 노출만 다르게 한다.
 *
 * 🔴 **토큰은 여기 없다.** 이 스토어는 「누구로 로그인했는가」만 들고 있고,
 *    access/refresh 토큰 보관은 `@ctcells/api` 의 토큰 저장소가 쥔다(전송 계층의 책임).
 */
export type SessionStatus =
  /** 부팅 직후. 저장된 세션을 복구해 보는 중이다 — 화면은 **로딩**으로 막는다 */
  'unknown' | 'authenticated' | 'anonymous'

/**
 * 왜 익명으로 떨어졌는가 — **로그인 화면이 한 줄 말하기 위한 축**이다.
 *
 * 🔴 **서버가 가를 수 있는 것만 둔다.** `/auth/refresh` 는 폐기(`revoked`) · 무활동 만료 ·
 *    절대 만료를 **전부 401 `AUTH_FAILED` 하나로** 답한다 — `ctcells-core`
 *    `app/services/auth.py` 의 `SessionRejection` 이 「거부 사유는 **응답에서는 구분하지
 *    않는다** — 로그용이다」라고 못 박았고, `app/views/auth.py` 의 두 갈래가 둘 다
 *    `error_response(ErrorCode.AUTH_FAILED, status_code=401)` 로 나간다(2026-09-22 코드·실물 확인).
 *    ⇒ 「관리자가 끊었습니다」 같은 값을 **만들어 넣지 않는다.** 그건 프론트가 상태를 추론하는 것이다.
 * ⚠ 전송 오류(서버 다운 · 회선 끊김)에는 사유를 달지 않는다. 그건 만료가 아니고,
 *   「만료되었습니다」로 적으면 «못 봤다»를 «확정된 사실»로 접는 것이다.
 */
export type SessionEndReason =
  /** 세션이 더 이상 유효하지 않다(만료 · 폐기 — 서버가 안 가른다) */
  | 'expired'
  /** 사람이 직접 로그아웃했다 */
  | 'logout'

interface SessionState {
  status: SessionStatus
  /** 로그인 응답의 `TokenResponse.user`. refresh 응답에는 안 실려서 우리가 들고 있는다 */
  user: User | null
  sessionId: string | null
  /**
   * `GET /auth/me` 가 준 operationId 목록. **`null` = 아직 못 받았다**(로그인 직후 · 실패).
   * 🔴 세션 id 가 바뀌면 버린다 — 남의 세션의 목록으로 판정하지 않는다.
   */
  operations: readonly string[] | null
  /**
   * 노출용 권한 5종. 🔴 **`operations` 가 있으면 그것으로, 없으면 역할 표로** 판정한다
   * (`resolvePermissions` · `permissionOperations.ts`). 집행은 서버 403 이다.
   */
  permissions: readonly Permission[]
  /**
   * 마지막으로 익명이 된 까닭. 🔴 **한 번 보이고 지운다**(`clearEndReason`).
   * ⚠ 메모리에만 둔다 — 브라우저 저장소에 남기면 다음 사람이 남의 로그아웃 사유를 본다.
   */
  endReason: SessionEndReason | null
  /**
   * ⚠ `operations` 를 생략하면 **같은 세션일 때만** 쥐고 있던 목록을 유지한다(refresh 가 매번 부른다).
   *   세션 id 가 바뀌면 `null` 로 돌아가 역할 표로 떨어진다.
   */
  setAuthenticated(
    user: User | null,
    sessionId: string | null,
    operations?: readonly string[] | null,
  ): void
  /** `/auth/me` 결과를 싣는다. 권한도 같이 다시 판정한다 */
  setOperations(operations: readonly string[] | null): void
  setAnonymous(reason?: SessionEndReason): void
  setStatus(status: SessionStatus): void
  clearEndReason(): void
  reset(): void
}

/**
 * 🔴 부팅 상태는 **저장된 refresh 토큰이 있느냐**로 갈린다.
 *   - 있다 → `unknown`(로딩). 조용히 갱신하는 동안 화면을 막는다 — 권한을 모르는 채로 그리지 않는다
 *   - 없다 → 바로 `anonymous`. 갱신할 것이 없는데 화면을 막을 이유가 없다
 */
function initialStatus(): SessionStatus {
  return safeStorage.read(AUTH_STORAGE_KEYS.refresh, 'session') === null ? 'anonymous' : 'unknown'
}

export const useSessionStore = create<SessionState>((set) => ({
  status: initialStatus(),
  user: null,
  sessionId: null,
  operations: null,
  permissions: [],
  endReason: null,
  setAuthenticated: (user, sessionId, operations) =>
    set((state) => {
      const kept =
        operations !== undefined
          ? operations
          : state.sessionId === sessionId
            ? state.operations
            : null
      return {
        status: 'authenticated',
        user,
        sessionId,
        operations: kept,
        permissions: resolvePermissions(user?.role, kept),
        // 로그인이 성공하면 지난 사유는 지운다 — 다음 로그아웃까지 남아 있을 이유가 없다
        endReason: null,
      }
    }),
  setOperations: (operations) =>
    set((state) => ({
      operations,
      permissions: resolvePermissions(state.user?.role, operations),
    })),
  /**
   * 🔴 **사유를 안 주면 «없음»이다.** 부팅 때 저장된 세션이 아예 없는 경우가 이쪽이고,
   *    그때 「만료되었습니다」가 뜨면 처음 온 사람에게 거짓말을 하는 것이다.
   */
  setAnonymous: (reason) =>
    set({
      status: 'anonymous',
      user: null,
      sessionId: null,
      operations: null,
      permissions: [],
      endReason: reason ?? null,
    }),
  setStatus: (status) => set({ status }),
  clearEndReason: () => set({ endReason: null }),
  reset: () =>
    set({
      status: initialStatus(),
      user: null,
      sessionId: null,
      operations: null,
      permissions: [],
      endReason: null,
    }),
}))
